import { useState } from "react";

import "./stylesPersonalInfo.css";

import VerifiedIcon from "../../../../assets/icons/ic_verified.svg";
import {
  ClockCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  FacebookOutlined,
  GoogleOutlined,
  AppleOutlined,
} from "@ant-design/icons";

import { Modal, Skeleton, message } from "antd";
import dayjs from "dayjs";
import { useQueryClient } from "@tanstack/react-query";
import { convertToUserTimezone,
  getAdminNow,
} from "../../../../utils/dateService";

import AddNoteModal from "./AddNoteModal/AddNoteModal";
import EditNoteModal from "./EditNoteModal/EditNoteModal";
import Notes from "./Notes/Notes";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../../../utils/ErrorUtils";
import { getKycStatusPresentation } from "../../../../utils/kycStatus";

import { useGetUserNotes } from "../../../../queries/user-notes/useGetUserNotes";
import { useCreateUserNote } from "../../../../queries/user-notes/useCreateUserNote";
import { useEditUserNote } from "../../../../queries/user-notes/useEditUserNote";
import { usePinUserNote } from "../../../../queries/user-notes/usePinUserNote";
import { useUnpinUserNote } from "../../../../queries/user-notes/useUnpinUserNote";
import { useDeleteUserNote } from "../../../../queries/user-notes/useDeleteUserNote";
import { userNotesQueryKey } from "../../../../queries/user-notes/types";

function PersonalInfo({ data, isLoading }) {
  const {
    user_id,
    username,
    first_name,
    last_name,
    email,
    email_verified,
    social_signup_platform,
    phone_number,
    date_joined,
    registration_subregion,
    kyc_status,
    last_suspend_reason,
    initial_deposit_ts,
    profile_photo,
    new_terms_accepted,
    deleted_by,
    utm_source,
    utm_medium,
    utm_campaign,
  } = data;

  const queryClient = useQueryClient();

  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [isEditNoteModalOpen, setIsEditNoteModalOpen] = useState(false);
  const [editNote, setEditNote] = useState(null);
  const [pendingNoteId, setPendingNoteId] = useState(null);

  const showAddNoteModal = () => setIsAddNoteModalOpen(true);
  const handleAddNoteModalCancel = () => setIsAddNoteModalOpen(false);

  const showEditNoteModal = (note) => {
    setEditNote(note);
    setIsEditNoteModalOpen(true);
  };
  const handleEditNoteModalCancel = () => setIsEditNoteModalOpen(false);

  const notesQuery = useGetUserNotes(user_id);
  const notes = notesQuery.data?.notes ?? [];

  const invalidateNotes = () => {
    if (!user_id) return;
    queryClient.invalidateQueries({ queryKey: userNotesQueryKey(user_id) });
  };

  const createNoteMutation = useCreateUserNote();
  const editNoteMutation = useEditUserNote();
  const pinNoteMutation = usePinUserNote();
  const unpinNoteMutation = useUnpinUserNote();
  const deleteNoteMutation = useDeleteUserNote();

  const handleAddNote = (body) => {
    if (!user_id) return;
    createNoteMutation.mutate(
      { user_id, body },
      {
        onSuccess: () => {
          invalidateNotes();
          setIsAddNoteModalOpen(false);
          message.success(AppStrings.NOTE_ADDED);
        },
        onError: (error) => {
          message.error(
            getApiErrorMessage(error, AppStrings.NOTE_ACTION_FAILED),
          );
        },
      },
    );
  };

  const handleEditNote = (noteId, body) => {
    if (!user_id) return;
    setPendingNoteId(noteId);
    editNoteMutation.mutate(
      { user_id, note_id: noteId, body },
      {
        onSuccess: () => {
          invalidateNotes();
          setIsEditNoteModalOpen(false);
          message.success(AppStrings.NOTE_UPDATED);
        },
        onError: (error) => {
          message.error(
            getApiErrorMessage(error, AppStrings.NOTE_ACTION_FAILED),
          );
        },
        onSettled: () => setPendingNoteId(null),
      },
    );
  };

  const handleDeleteNote = (noteId) => {
    if (!user_id) return;
    setPendingNoteId(noteId);
    deleteNoteMutation.mutate(
      { user_id, note_id: noteId },
      {
        onSuccess: () => {
          invalidateNotes();
          message.success(AppStrings.NOTE_DELETED);
        },
        onError: (error) => {
          message.error(
            getApiErrorMessage(error, AppStrings.NOTE_ACTION_FAILED),
          );
        },
        onSettled: () => setPendingNoteId(null),
      },
    );
  };

  const handlePinNote = (noteId) => {
    if (!user_id) return;
    setPendingNoteId(noteId);
    pinNoteMutation.mutate(
      { user_id, note_id: noteId },
      {
        onSuccess: () => {
          invalidateNotes();
          message.success(AppStrings.NOTE_PINNED);
        },
        onError: (error) => {
          // 409 means the note is already pinned in the BE — refetch and
          // surface a neutral toast rather than a destructive error.
          if (error?.response?.status === 409) {
            invalidateNotes();
            message.info(AppStrings.NOTE_ALREADY_PINNED);
            return;
          }
          message.error(
            getApiErrorMessage(error, AppStrings.NOTE_ACTION_FAILED),
          );
        },
        onSettled: () => setPendingNoteId(null),
      },
    );
  };

  const handleUnpinNote = (noteId) => {
    if (!user_id) return;
    setPendingNoteId(noteId);
    unpinNoteMutation.mutate(
      { user_id, note_id: noteId },
      {
        onSuccess: () => {
          invalidateNotes();
          message.success(AppStrings.NOTE_UNPINNED);
        },
        onError: (error) => {
          message.error(
            getApiErrorMessage(error, AppStrings.NOTE_ACTION_FAILED),
          );
        },
        onSettled: () => setPendingNoteId(null),
      },
    );
  };

  const handleRetryNotes = () => {
    invalidateNotes();
  };
  const formatFirstFTD = (dateString) => {
    return convertToUserTimezone(dateString, "MM/DD/YYYY");
  };

  const formatDaysSince = (date) => {
    const days = getAdminNow().diff(dayjs(date), "days");

    if (days > 100) {
      return "100+";
    }

    return days;
  };

  const kycStatus = getKycStatusPresentation(kyc_status);

  const renderKycStatusIcon = () => {
    if (kycStatus.tone === "success") {
      return <img src={VerifiedIcon} alt="verified icon" />;
    }
    if (kycStatus.tone === "error") {
      return <CloseCircleOutlined className="kyc-status-icon-error" />;
    }
    if (kycStatus.tone === "pending") {
      return <ClockCircleOutlined className="kyc-status-icon-pending" />;
    }
    return <WarningOutlined className="kyc-status-icon-neutral" />;
  };

  return (
    <div className="personal-info-container">
      <Modal
        closable={false}
        open={isAddNoteModalOpen}
        onCancel={handleAddNoteModalCancel}
        maskClosable={!createNoteMutation.isPending}
        keyboard={!createNoteMutation.isPending}
        destroyOnHidden
        footer={null}
        width={400}
      >
        <AddNoteModal
          handleCancel={handleAddNoteModalCancel}
          handleNotesChange={handleAddNote}
          isSubmitting={createNoteMutation.isPending}
        />
      </Modal>
      {editNote && (
        <Modal
          closable={false}
          open={isEditNoteModalOpen}
          onCancel={handleEditNoteModalCancel}
          maskClosable={!editNoteMutation.isPending}
          keyboard={!editNoteMutation.isPending}
          destroyOnHidden
          footer={null}
          width={400}
        >
          <EditNoteModal
            handleCancel={handleEditNoteModalCancel}
            handleNotesChange={handleEditNote}
            editNote={editNote}
            isSubmitting={editNoteMutation.isPending}
          />
        </Modal>
      )}
      <div className="personal-info-header">
        <h2>{AppStrings.PERSONAL_INFO}</h2>
      </div>
      {isLoading ? (
        <Skeleton loading={true} paragraph={{ rows: 10 }} active />
      ) : (
        <>
          {last_suspend_reason && (
            <div className="suspended-reason-container">
              <div className="suspended-reason-header">
                <WarningOutlined className="warning-icon" />
                <p>{AppStrings.SUSPENSION_REASON}</p>
              </div>
              <p className="suspended-reason-text">{last_suspend_reason}</p>
            </div>
          )}
          {deleted_by && (
            <div className="suspended-reason-container deleted-account-container">
              <div className="suspended-reason-header">
                <CloseCircleOutlined className="warning-icon" />
                <p>{AppStrings.ACCOUNT_DELETED_BY}:</p>
              </div>
              <p className="suspended-reason-text">{deleted_by}</p>
            </div>
          )}
          <div className="personal-info-body">
            <div className="personal-info-row">
              <div className="personal-info">
                <p className="first">{AppStrings.USER_ID}</p>
                <p className="second">{user_id}</p>
              </div>
              <div className="personal-info">
                <p className="first">{AppStrings.USER_NAME}</p>
                <p className="second">{username}</p>
              </div>
            </div>
            <div className="personal-info-row">
              <div className="personal-info">
                <p className="first">{AppStrings.FIRST_NAME}</p>
                <p className="second">{first_name}</p>
              </div>
              <div className="personal-info">
                <p className="first">{AppStrings.LAST_NAME}</p>
                <p className="second">{last_name}</p>
              </div>
            </div>
            <div className="personal-info-row">
              <div className="personal-info">
                <p className="first">{AppStrings.EMAIL}</p>
                <div className="email-container">
                  <p className="second">{email}</p>
                  {email_verified ? (
                    <>
                      <img
                        src={VerifiedIcon}
                        alt="verified icon"
                        className="verified-icon"
                      />
                      <p className="verified-text">{AppStrings.VERIFIED}</p>
                    </>
                  ) : (
                    <>
                      <ClockCircleOutlined className="email-status-icon-pending" />
                      <p className="verified-text">{AppStrings.PENDING}</p>
                    </>
                  )}
                </div>
              </div>
              <div className="personal-info">
                <p className="first">{AppStrings.PHONE}</p>
                <p className="second">{phone_number}</p>
              </div>
            </div>
            <div className="personal-info-row">
              <div className="personal-info">
                <p className="first">{AppStrings.DATE_OF_REGISTRATION}</p>
                <p className="second">
                  {convertToUserTimezone(date_joined, "MMM DD, YYYY")}
                  <span className="number-of-days">
                    {formatDaysSince(date_joined)} {AppStrings.DAYS}
                  </span>
                </p>
              </div>
              <div className="personal-info">
                <p className="first">{AppStrings.FTD_DATE}</p>
                <p className="second">
                  {initial_deposit_ts
                    ? formatFirstFTD(initial_deposit_ts)
                    : "Has Not Deposited"}
                </p>
              </div>
            </div>
            <div className="personal-info-row">
              <div className="personal-info">
                <p className="first">{AppStrings.ACCEPTED_TOU}?</p>
                <p className="second w-200">
                  {new_terms_accepted?.status ? AppStrings.YES : AppStrings.NO}
                </p>
              </div>
              <div className="personal-info">
                <p className="first">{AppStrings.KYC}</p>
                <p className="second">
                  {renderKycStatusIcon()}
                  {kycStatus.label}
                </p>
              </div>
            </div>
            <div className="personal-info-row">
              <div className="personal-info">
                <p className="first">{AppStrings.STATE}</p>
                <p className="second w-200">
                  {registration_subregion || AppStrings.NOT_AVAILABLE}
                </p>
              </div>
              <div className="personal-info">
                <p className="first">{AppStrings.SOCIAL_SIGNUP_PLATFORM}</p>
                <p className="second w-200 social-signup-platform-text">
                  {social_signup_platform === "facebook" ? (
                    <FacebookOutlined
                      className="social-signup-platform-icon"
                      alt="facebook"
                    />
                  ) : social_signup_platform === "google" ? (
                    <GoogleOutlined
                      className="social-signup-platform-icon"
                      alt="google"
                    />
                  ) : social_signup_platform === "apple" ? (
                    <AppleOutlined
                      className="social-signup-platform-icon"
                      alt="apple"
                    />
                  ) : null}
                  {social_signup_platform || AppStrings.NOT_AVAILABLE}
                </p>
              </div>
            </div>
            <div className="personal-info-row">
              <div className="personal-info">
                <p className="first">{AppStrings.UTM_SOURCE}</p>
                <p className="second w-200">
                  {utm_source || AppStrings.NOT_AVAILABLE}
                </p>
              </div>
              <div className="personal-info">
                <p className="first">{AppStrings.UTM_MEDIUM}</p>
                <p className="second w-200">
                  {utm_medium || AppStrings.NOT_AVAILABLE}
                </p>
              </div>
            </div>
            <div className="personal-info-row">
              <div className="personal-info">
                <p className="first">{AppStrings.UTM_CAMPAIGN}</p>
                <p className="second w-200">
                  {utm_campaign || AppStrings.NOT_AVAILABLE}
                </p>
              </div>
            </div>
          </div>
          <div className="personal-info-footer">
            <Notes
              notes={notes}
              profilePhoto={profile_photo}
              isLoading={notesQuery.isLoading}
              isError={notesQuery.isError}
              pendingNoteId={pendingNoteId}
              onAddNote={showAddNoteModal}
              onEditNote={showEditNoteModal}
              onDeleteNote={handleDeleteNote}
              onPinNote={handlePinNote}
              onUnpinNote={handleUnpinNote}
              onRetry={handleRetryNotes}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default PersonalInfo;
